// ─────────────────────────────────────────────────────────────
//  Refacturări (v9.10 / T5) — facturile de furnizor cu refacturare,
//  vizibile clientului DUPĂ verificarea echipei LEASEIN.
//  Administratorul de flotă își poate exprima acordul — răspunsul e
//  INFORMATIV (se vede în platforma internă, nu blochează procesarea).
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "./api";
import { formatData } from "../utils/expirare";

const FILTRE = [
  { id: "toate", text: "Toate" },
  { id: "fara-raspuns", text: "Fără răspuns" },
  { id: "aprobate", text: "De acord" },
  { id: "respinse", text: "Nu sunt de acord" },
];

function bani(n) {
  const x = Number(n);
  if (isNaN(x)) return "—";
  return `${x.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
}

export default function Refacturari() {
  const [lista, setLista] = useState([]);
  const [stare, setStare] = useState("loading");
  const [filtru, setFiltru] = useState("toate");
  const [expandat, setExpandat] = useState(null);   // id-ul facturii cu elementele deschise
  const [seTrimite, setSeTrimite] = useState(null); // `${id}:${DA|NU}` cât timp POST-ul e în zbor

  useEffect(() => {
    apiFetch("/refacturari")
      .then((d) => {
        setLista(d.refacturari || []);
        setStare("ok");
      })
      .catch(() => setStare("error"));
  }, []);

  const afisate = useMemo(() => {
    return lista.filter((r) => {
      if (filtru === "fara-raspuns") return !r.aprobatClient;
      if (filtru === "aprobate") return r.aprobatClient === "DA";
      if (filtru === "respinse") return r.aprobatClient === "NU";
      return true;
    });
  }, [lista, filtru]);

  const totalRefacturat = useMemo(
    () => lista.reduce((s, r) => s + (Number(r.sumaRefacturata) || 0), 0),
    [lista]
  );
  const faraRaspuns = useMemo(
    () => lista.filter((r) => !r.aprobatClient).length,
    [lista]
  );

  async function trimiteAprobare(r, aprobat) {
    if (seTrimite) return;
    setSeTrimite(`${r.id}:${aprobat}`);
    try {
      await apiFetch(`/refacturari/${r.id}/aprobare`, {
        method: "POST",
        body: JSON.stringify({ aprobat }),
      });
      setLista((prev) =>
        prev.map((x) =>
          x.id === r.id
            ? { ...x, aprobatClient: aprobat, aprobatClientData: new Date().toISOString() }
            : x
        )
      );
    } catch (e) {
      alert(e.message || "Nu am putut înregistra răspunsul. Reîncearcă.");
    } finally {
      setSeTrimite(null);
    }
  }

  return (
    <div className="facturi refact">
      <div className="dash__head">
        <h1>Refacturări</h1>
        <p>
          Facturile de la furnizori (service, tractare etc.) pe care LEASEIN le
          refacturează către firma ta, verificate în prealabil de echipa noastră.
        </p>
      </div>

      <div className="refact__info">
        <span className="refact__info-icon">ℹ</span>
        <span>
          Răspunsul tău este <strong>informativ</strong> — ajută echipa LEASEIN
          să știe din timp dacă ai observații. Pentru obiecții detaliate,
          folosește secțiunea <em>Solicitări</em>.
        </span>
      </div>

      {stare === "loading" && <SkeletonRefacturari />}

      {stare === "error" && (
        <div className="portal__loading">
          Nu am putut încărca refacturările. Reîncearcă mai târziu.
        </div>
      )}

      {stare === "ok" && lista.length === 0 && (
        <div className="portal__loading">
          Nu există refacturări momentan. Vor apărea aici după ce echipa LEASEIN
          verifică facturile care privesc mașinile tale.
        </div>
      )}

      {stare === "ok" && lista.length > 0 && (
        <>
          <div className="facturi__sumar">
            <span className={`dot ${faraRaspuns > 0 ? "dot--galben" : "dot--verde"}`} />
            {lista.length} {lista.length === 1 ? "factură" : "facturi"} de refacturare,
            în total <strong>{bani(totalRefacturat)}</strong>
            {faraRaspuns > 0 && (
              <> — <strong>{faraRaspuns}</strong> fără răspuns</>
            )}
            .
          </div>

          <div className="chips">
            {FILTRE.map((f) => (
              <button
                key={f.id}
                className={`chip ${filtru === f.id ? "chip--active" : ""}`}
                onClick={() => setFiltru(f.id)}
              >
                {f.text}
              </button>
            ))}
          </div>

          <p className="masini__count">
            {afisate.length} {afisate.length === 1 ? "factură" : "facturi"}
            {filtru !== "toate" ? ` (din ${lista.length})` : ""}
          </p>

          {afisate.length === 0 ? (
            <div className="portal__loading">Nicio factură în această categorie.</div>
          ) : (
            <div className="facturi__tabel" key={filtru}>
              <div className="facturi__cap refact__grid">
                <span>Data</span>
                <span>Furnizor</span>
                <span>Vehicul</span>
                <span>Tip</span>
                <span>Sumă refacturată</span>
                <span>Acordul tău</span>
              </div>
              {afisate.map((r) => (
                <React.Fragment key={r.id}>
                  <div className="facturi__rand refact__grid">
                    <span className="facturi__data" data-label="Data">
                      {formatData(r.data) || "—"}
                    </span>
                    <span data-label="Furnizor">
                      <span className="facturi__masina">{r.furnizor || "—"}</span>
                      {r.nrFactura ? (
                        <span className="refact__nr"> #{r.nrFactura}</span>
                      ) : null}
                    </span>
                    <span className="facturi__data" data-label="Vehicul">
                      {r.vehicul || "—"}
                    </span>
                    <span data-label="Tip">
                      <span className="tipf tipf--refacturare">{r.tip || "—"}</span>
                    </span>
                    <span className="facturi__suma" data-label="Sumă refacturată">
                      {bani(r.sumaRefacturata)}
                      {Number(r.sumaRefacturata) > 0 &&
                        r.sumaTotala &&
                        Math.abs(Number(String(r.sumaTotala).replace(",", ".")) - Number(r.sumaRefacturata)) > 0.005 && (
                          <span className="refact__din-total">
                            {" "}din {r.sumaTotala} lei
                          </span>
                        )}
                      {r.elemente && r.elemente.length > 0 && (
                        <button
                          className="refact__toggle"
                          onClick={() =>
                            setExpandat(expandat === r.id ? null : r.id)
                          }
                        >
                          {expandat === r.id ? "▾" : "▸"} {r.elemente.length}{" "}
                          {r.elemente.length === 1 ? "element" : "elemente"}
                        </button>
                      )}
                    </span>
                    <span className="refact__aprobare" data-label="Acordul tău">
                      {r.aprobatClient === "DA" ? (
                        <span className="pill pill--verde" title={r.aprobatClientData ? `răspuns la ${formatData(r.aprobatClientData)}` : undefined}>
                          ✓ De acord
                        </span>
                      ) : r.aprobatClient === "NU" ? (
                        <span className="pill pill--rosu" title={r.aprobatClientData ? `răspuns la ${formatData(r.aprobatClientData)}` : undefined}>
                          ✗ Nu sunt de acord
                        </span>
                      ) : (
                        <span className="refact__btns">
                          <button
                            className="btn-pdf"
                            disabled={!!seTrimite}
                            onClick={() => trimiteAprobare(r, "DA")}
                          >
                            {seTrimite === `${r.id}:DA` ? "…" : "De acord"}
                          </button>
                          <button
                            className="btn-pdf refact__btn-nu"
                            disabled={!!seTrimite}
                            onClick={() => trimiteAprobare(r, "NU")}
                          >
                            {seTrimite === `${r.id}:NU` ? "…" : "Nu"}
                          </button>
                        </span>
                      )}
                    </span>
                  </div>
                  {expandat === r.id && r.elemente && r.elemente.length > 0 && (
                    <div className="refact__elemente">
                      <div className="refact__elemente-titlu">
                        Elemente refacturate de pe această factură:
                      </div>
                      {r.elemente.map((el, i) => (
                        <div className="refact__element" key={i}>
                          <span>{el.descriere || "—"}</span>
                          <span className="refact__element-suma">{bani(el.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SkeletonRefacturari() {
  return (
    <div className="facturi__tabel" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div className="facturi__rand refact__grid" key={i}>
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <span key={j}>
              <span className="skel skel--line" style={{ height: 14, width: "80%" }} />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
